import React, { FC, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  IconButton,
  Icon,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
  MenuItem,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import { UnitSelect } from "../../../../components/UnitSelect/UnitSelect";
import { convertUnits } from "../../../../utils/units";

export interface ProcessedItemOption {
  _id: string;
  name: string;
  batch_id: string;
  production_date: number;
  expiry_date: number;
  quantity: number;
  remaining_quantity: number;
  unit: string;
  storage_place: string;
  recipe_name: string;
  cost_per_unit: number;
}

interface ProcessedItemEntry {
  item: ProcessedItemOption | null;
  quantity: number;
  unit: string;
  notes: string;
}

interface ProcessedItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ProcessedItemEntry[]) => void;
  processedItems: ProcessedItemOption[];
  loadingItems: boolean;
  mode: "add" | "edit";
  currentItems?: Array<{
    processed_item_id: string;
    quantity: number;
    unit: string;
  }>;
  editData?: ProcessedItemEntry;
}

// Add theme colors at the top after imports
const THEME_COLORS = {
  primary: '#0891B2',  // Cyan-600
  primaryHover: '#0E7490', // Cyan-700
  secondary: '#64748B', // Slate-500
  danger: '#DC2626', // Red-600
  dangerHover: '#B91C1C', // Red-700
  success: '#059669', // Emerald-600
  background: {
    main: '#FFFFFF',
    light: '#F8FAFC',
    card: '#FFFFFF',
  },
  border: {
    light: alpha('#0891B2', 0.1),
    main: alpha('#0891B2', 0.2),
  },
  text: {
    primary: '#1E293B', // Slate-800
    secondary: '#64748B', // Slate-500
    light: '#94A3B8', // Slate-400
  }
};

const ProcessedItemCard: FC<{
  entry: ProcessedItemEntry;
  index: number;
  onQuantityChange: (index: number, value: number) => void;
  onUnitChange: (index: number, unit: string) => void;
  onNotesChange: (index: number, notes: string) => void;
  onRemove: (index: number) => void;
}> = ({
  entry,
  index,
  onQuantityChange,
  onUnitChange,
  onNotesChange,
  onRemove,
}) => {
  const theme = useTheme();
  const item = entry.item;
  if (!item) return null;

  const isExpired = dayjs(item.expiry_date).isBefore(dayjs());
  const isExpiring = dayjs(item.expiry_date).isBefore(dayjs().add(30, "day"));
  const hasStock = item.remaining_quantity > 0;
  const daysUntilExpiry = dayjs(item.expiry_date).diff(dayjs(), "day");

  const remainingInSelectedUnit =
    entry.unit !== item.unit
      ? convertUnits(item.remaining_quantity, item.unit, entry.unit) ||
        item.remaining_quantity
      : item.remaining_quantity;

  const convertedQuantity =
    entry.unit !== item.unit
      ? convertUnits(entry.quantity, entry.unit, item.unit) || entry.quantity
      : entry.quantity;
  const exceedsStock = convertedQuantity > item.remaining_quantity;

  return (
    <Card
      sx={{
        bgcolor: THEME_COLORS.background.light,
        border: `1px solid ${THEME_COLORS.border.light}`,
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: THEME_COLORS.primary,
          boxShadow: `0 0 0 1px ${THEME_COLORS.primary}`,
        },
      }}
    >
      <CardContent sx={{ p: "16px !important" }}>
        <Stack spacing={2}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: THEME_COLORS.text.primary,
                fontFamily: 'Montserrat',
                fontWeight: 600,
              }}
            >
              {item.name}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              sx={{
                color: THEME_COLORS.danger,
                "&:hover": {
                  bgcolor: alpha(THEME_COLORS.danger, 0.08),
                },
              }}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Box>

          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                label={`${item.remaining_quantity}/${item.quantity} ${item.unit}`}
                sx={{
                  bgcolor: hasStock 
                    ? alpha(THEME_COLORS.success, 0.08)
                    : alpha(THEME_COLORS.danger, 0.08),
                  color: hasStock 
                    ? THEME_COLORS.success
                    : THEME_COLORS.danger,
                  border: `1px solid ${hasStock 
                    ? alpha(THEME_COLORS.success, 0.2)
                    : alpha(THEME_COLORS.danger, 0.2)}`,
                  borderRadius: 1,
                }}
              />
              <Tooltip
                title={
                  isExpired
                    ? "Expired"
                    : isExpiring
                    ? `Expires in ${daysUntilExpiry} days`
                    : `Expires in ${daysUntilExpiry} days`
                }
              >
                <Icon
                  fontSize="small"
                  sx={{
                    color: isExpired 
                      ? THEME_COLORS.danger
                      : isExpiring 
                      ? THEME_COLORS.secondary
                      : THEME_COLORS.success
                  }}
                >
                  {isExpired
                    ? "error"
                    : isExpiring
                    ? "warning"
                    : "check_circle"}
                </Icon>
              </Tooltip>
              <Typography 
                variant="caption" 
                sx={{ color: THEME_COLORS.text.secondary }}
              >
                Expires: {dayjs(item.expiry_date).format("MMM D, YYYY")}
              </Typography>
            </Box>

            <Box>
              <LinearProgress
                variant="determinate"
                value={(item.remaining_quantity / item.quantity) * 100}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(THEME_COLORS.border.main, 0.2),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 2,
                    bgcolor: exceedsStock
                      ? THEME_COLORS.danger
                      : THEME_COLORS.primary,
                  },
                }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 0.5 }}
              >
                <Stack direction="row" spacing={2}>
                  <Typography 
                    variant="caption" 
                    sx={{ color: THEME_COLORS.text.secondary }}
                  >
                    Storage: {item.storage_place || "Not specified"}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ color: THEME_COLORS.text.secondary }}
                  >
                    Recipe: {item.recipe_name || "No recipe"}
                  </Typography>
                </Stack>
                <Typography 
                  variant="caption" 
                  sx={{ color: THEME_COLORS.primary }}
                >
                  {item.cost_per_unit.toFixed(3)} BHD/{item.unit}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Quantity"
              type="number"
              value={entry.quantity || ""}
              onChange={(e) => onQuantityChange(index, Number(e.target.value))}
              required
              error={exceedsStock}
              helperText={
                exceedsStock
                  ? `Maximum available: ${remainingInSelectedUnit.toFixed(2)} ${
                      entry.unit
                    }`
                  : undefined
              }
              inputProps={{ step: "0.0001", min: 0 }}
              InputProps={{
                endAdornment: (
                  <UnitSelect
                    value={entry.unit}
                    onChange={(value) => onUnitChange(index, value)}
                    sourceUnit={item.unit}
                  />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: THEME_COLORS.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: THEME_COLORS.primary,
                  }
                }
              }}
            />
            {entry.quantity > 0 && (
              <Box
                sx={{
                  minWidth: 120,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ color: THEME_COLORS.text.secondary }}
                >
                  Cost: {(convertedQuantity * item.cost_per_unit).toFixed(3)} BHD
                </Typography>
              </Box>
            )}
          </Stack>

          <TextField
            fullWidth
            size="small"
            label="Notes"
            multiline
            rows={2}
            value={entry.notes}
            onChange={(e) => onNotesChange(index, e.target.value)}
            placeholder="Add any special instructions or notes"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: THEME_COLORS.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: THEME_COLORS.primary,
                }
              }
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export const ProcessedItemDialog: FC<ProcessedItemDialogProps> = ({
  open,
  onClose,
  onSave,
  processedItems,
  loadingItems,
  mode = "add",
  currentItems = [],
  editData,
}) => {
  const theme = useTheme();
  const [entries, setEntries] = useState<ProcessedItemEntry[]>([
    {
      item: null,
      quantity: 0,
      unit: "",
      notes: "",
    },
  ]);

  // Filter and sort items
  const availableItems = React.useMemo(() => {
    return processedItems
      .filter((item) => {
        if (mode === "edit" && editData?.item?._id === item._id) return true;
        if (
          currentItems.some((current) => current.processed_item_id === item._id)
        )
          return false;
        return true;
      })
      .sort((a, b) => {
        const aExpired = dayjs(a.expiry_date).isBefore(dayjs());
        const bExpired = dayjs(b.expiry_date).isBefore(dayjs());
        if (aExpired !== bExpired) return aExpired ? 1 : -1;
        return b.remaining_quantity - a.remaining_quantity;
      });
  }, [processedItems, mode, editData, currentItems]);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && editData) {
        setEntries([editData]);
      } else {
        setEntries([
          {
            item: null,
            quantity: 0,
            unit: "",
            notes: "",
          },
        ]);
      }
    }
  }, [open, mode, editData]);

  const handleAddEntry = () => {
    if (mode === "edit") return;
    setEntries((prev) => [
      ...prev,
      {
        item: null,
        quantity: 0,
        unit: "",
        notes: "",
      },
    ]);
  };

  const handleRemoveEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (
    index: number,
    item: ProcessedItemOption | null
  ) => {
    if (!item) return;
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        item,
        unit: item.unit,
      };
      return updated;
    });
  };

  const handleQuantityChange = (index: number, value: number) => {
    setEntries((prev) => {
      const updated = [...prev];
      const entry = updated[index];
      const item = entry.item;

      if (item) {
        const convertedQuantity =
          entry.unit !== item.unit
            ? convertUnits(value, entry.unit, item.unit) || value
            : value;

        if (convertedQuantity > item.remaining_quantity) {
          value =
            convertUnits(item.remaining_quantity, item.unit, entry.unit) ||
            value;
        }
      }

      updated[index] = { ...entry, quantity: value };
      return updated;
    });
  };

  const handleUnitChange = (index: number, unit: string) => {
    setEntries((prev) => {
      const updated = [...prev];
      const entry = updated[index];
      const item = entry.item;

      if (item && entry.quantity > 0) {
        const convertedQuantity = convertUnits(
          entry.quantity,
          entry.unit,
          unit
        );
        if (convertedQuantity !== null) {
          updated[index] = { ...entry, unit, quantity: convertedQuantity };
          return updated;
        }
      }

      updated[index] = { ...entry, unit };
      return updated;
    });
  };

  const handleNotesChange = (index: number, notes: string) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], notes };
      return updated;
    });
  };

  const handleSave = () => {
    const validEntries = entries.filter(
      (entry) => entry.item && entry.quantity > 0
    );
    onSave(validEntries);
    onClose();
  };

  const isValid = entries.some((entry) => entry.item && entry.quantity > 0);

  // Calculate total cost
  const totalCost = entries.reduce((sum, entry) => {
    if (!entry.item || !entry.quantity) return sum;
    const convertedQuantity =
      entry.unit !== entry.item.unit
        ? convertUnits(entry.quantity, entry.unit, entry.item.unit) ||
          entry.quantity
        : entry.quantity;
    return sum + convertedQuantity * entry.item.cost_per_unit;
  }, 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: THEME_COLORS.background.light,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${THEME_COLORS.border.light}`,
          px: 3,
          py: 2,
          bgcolor: THEME_COLORS.background.main,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: 'Montserrat',
                fontWeight: 600,
                color: THEME_COLORS.text.primary,
                mb: 0.5,
              }}
            >
              {mode === "edit" ? "Edit Processed Item" : "Add Processed Items"}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: THEME_COLORS.text.secondary }}
            >
              {mode === "edit"
                ? "Modify the processed item details"
                : "Select processed items and specify quantities"}
            </Typography>
          </Box>
          {mode === "add" && (
            <Button
              startIcon={<Icon>add</Icon>}
              onClick={handleAddEntry}
              variant="contained"
              sx={{
                bgcolor: THEME_COLORS.primary,
                '&:hover': {
                  bgcolor: THEME_COLORS.primaryHover,
                },
              }}
            >
              Add Another
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loadingItems ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress sx={{ color: THEME_COLORS.primary }} />
          </Box>
        ) : availableItems.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Icon 
              sx={{ 
                fontSize: 48, 
                color: alpha(THEME_COLORS.primary, 0.2),
                mb: 2 
              }}
            >
              inventory_2
            </Icon>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ 
                color: THEME_COLORS.text.primary,
                fontFamily: 'Montserrat',
                fontWeight: 600,
              }}
            >
              No Processed Items Available
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: THEME_COLORS.text.secondary }}
            >
              All processed items have been added or no items exist.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {entries.map((entry, index) => (
              <Box key={index}>
                <Box sx={{ mb: 2 }}>
                  <Autocomplete
                    value={entry.item}
                    onChange={(_e, newValue) => handleItemChange(index, newValue)}
                    options={availableItems}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Processed Item"
                        required
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: THEME_COLORS.primary,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: THEME_COLORS.primary,
                            }
                          }
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <MenuItem {...props}>
                        <Stack spacing={0.5} sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography 
                              variant="subtitle2"
                              sx={{ 
                                color: THEME_COLORS.text.primary,
                                fontWeight: 500,
                              }}
                            >
                              {option.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${option.remaining_quantity} ${option.unit}`}
                              sx={{
                                bgcolor: option.remaining_quantity > 0 
                                  ? alpha(THEME_COLORS.success, 0.08)
                                  : alpha(THEME_COLORS.danger, 0.08),
                                color: option.remaining_quantity > 0 
                                  ? THEME_COLORS.success
                                  : THEME_COLORS.danger,
                                border: `1px solid ${option.remaining_quantity > 0 
                                  ? alpha(THEME_COLORS.success, 0.2)
                                  : alpha(THEME_COLORS.danger, 0.2)}`,
                                borderRadius: 1,
                              }}
                            />
                            {dayjs(option.expiry_date).isBefore(dayjs()) && (
                              <Chip
                                size="small"
                                label="Expired"
                                sx={{
                                  bgcolor: alpha(THEME_COLORS.danger, 0.08),
                                  color: THEME_COLORS.danger,
                                  border: `1px solid ${alpha(THEME_COLORS.danger, 0.2)}`,
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </Box>
                          <Typography 
                            variant="caption" 
                            sx={{ color: THEME_COLORS.text.secondary }}
                          >
                            Recipe: {option.recipe_name}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    )}
                  />
                </Box>
                {entry.item && (
                  <ProcessedItemCard
                    entry={entry}
                    index={index}
                    onQuantityChange={handleQuantityChange}
                    onUnitChange={handleUnitChange}
                    onNotesChange={handleNotesChange}
                    onRemove={handleRemoveEntry}
                  />
                )}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${THEME_COLORS.border.light}`,
          px: 3,
          py: 2,
          bgcolor: THEME_COLORS.background.main,
        }}
      >
        <Box sx={{ flex: 1 }}>
          {totalCost > 0 && (
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: THEME_COLORS.primary,
                fontFamily: 'Montserrat',
                fontWeight: 600,
              }}
            >
              Total Cost: {totalCost.toFixed(3)} BHD
            </Typography>
          )}
        </Box>
        <Button 
          onClick={onClose}
          sx={{
            color: THEME_COLORS.text.primary,
            '&:hover': {
              bgcolor: alpha(THEME_COLORS.primary, 0.04),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid}
          startIcon={<Icon>save</Icon>}
          sx={{
            bgcolor: THEME_COLORS.primary,
            '&:hover': {
              bgcolor: THEME_COLORS.primaryHover,
            },
            '&.Mui-disabled': {
              bgcolor: alpha(THEME_COLORS.primary, 0.12),
              color: alpha(THEME_COLORS.primary, 0.26),
            },
          }}
        >
          {mode === "edit" ? "Save Changes" : "Add Selected Items"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
